import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Progress,
  Badge,
  Alert,
  Card,
  Input,
  SimpleGrid,
  Heading,
} from '@chakra-ui/react';
import { toaster } from '../../components/ui/toaster';
import { FiUpload, FiFile, FiX, FiSearch, FiEye, FiEyeOff, FiCheck, FiInfo } from 'react-icons/fi';
import { systemStandardsAPI } from '../../services/api';
import type { SystemStandard } from '../../types';
import { useTranslation } from 'react-i18next';
import RequiredEvidenceChecklist, { type ChecklistResponse } from './RequiredEvidenceChecklist';

// Mock evidence interface - add to types later
interface FieldCheckItem {
  field: string;
  label?: string;
  found: boolean;
  note?: string;
}

interface FieldCheckEntry {
  type_key: string;
  checks: FieldCheckItem[];
}

interface Evidence {
  id: string;
  filename: string;
  file_size: number;
  file_type: string;
  uploaded_at: string;
  content_summary?: string;
  /** Evidence-type keys this document plausibly provides (from backend classifier). */
  evidence_types?: string[];
  /** Merged (deterministic + LLM) structured-field check per evidence type. */
  field_checks?: FieldCheckEntry[];
  /** Raw LLM per-field verification (kept for reference; field_checks is the merged view). */
  field_verification?: FieldCheckEntry[];
  /** Rule-based accept/reject verdict from evidence verification, prior to mapping. */
  verification_status?: 'accepted' | 'rejected';
  /** LLM's plain-language explanation of the verification verdict. */
  verification_comments?: string;
  /** True once the user has chosen not to rely on this (rejected) evidence. */
  skipped_by_user?: boolean;
  /** Measure-level auto-link suggestions from backend pre-submit validation. */
  auto_detected_sections?: Record<string, { confidence?: number; matched_types?: string[]; reason?: string }>;
  confidence_score?: number;
  confidence_reason?: string;
}

interface SectionSubSubsection {
  id: string;
  title?: string;
  content?: string;
}

interface SectionSubsection {
  subsection_id: string;
  subsection_title: string;
  subsection_content?: string;
  sub_subsections?: SectionSubSubsection[];
}

interface SectionDataItem {
  id: string;
  title: string;
  subsections?: SectionSubsection[];
}

interface EvidenceLinkTarget {
  id: string;
  label: string;
  parentLabel: string;
}

interface ManualEvidenceLink {
  sectionId: string;
  evidenceIds: string[];
  typeKey?: string;
}

interface EvidenceUploadStepProps {
  selectedSections: string[];
  selectedSubsections?: { [key: string]: string[] };
  selectedStandard: SystemStandard | null;
  evidenceFiles: Evidence[];
  aiLinkedEvidence: Array<{
    sectionId: string;
    evidenceIds: string[];
  }>;
  manualEvidenceLinks: Array<{
    sectionId: string;
    evidenceIds: string[];
    typeKey?: string;
  }>;
  onEvidenceUpdate: (evidence: Evidence[]) => void;
  onAiLinksUpdate: (links: Array<{ sectionId: string; evidenceIds: string[] }>) => void;
  onManualLinksUpdate: (links: ManualEvidenceLink[]) => void;
}

const EvidenceUploadStep: React.FC<EvidenceUploadStepProps> = ({
  selectedSections,
  selectedSubsections,
  selectedStandard,
  evidenceFiles,
  aiLinkedEvidence,
  manualEvidenceLinks,
  onEvidenceUpdate,
  onAiLinksUpdate,
  onManualLinksUpdate,
}) => {
  const { t, i18n } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAiAnalysisOpen, setIsAiAnalysisOpen] = useState(false);
  const [requiredEvidenceData, setRequiredEvidenceData] = useState<ChecklistResponse | null>(null);

  // Dynamic section data loaded from the selected standard
  const [sectionData, setSectionData] = useState<SectionDataItem[]>([]);

  useEffect(() => {
    if (selectedStandard) {
      loadSections();
    }
  }, [selectedStandard]);

  const loadSections = async () => {
    if (!selectedStandard) return;
    try {
      const response = await systemStandardsAPI.getSections(selectedStandard.id.toString());
      const sections = response.sections || [];
      setSectionData(
        sections.map((s: any) => ({
          id: s.section_id || s.section || '',
          title: s.section_title || s.title || s.section_id || '',
          subsections: s.subsections || [],
        }))
      );
    } catch (error) {
      console.error('Error loading sections for evidence linking:', error);
    }
  };

  const [uploadProgressLabel, setUploadProgressLabel] = useState('');
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(15);
  const [mappingInProgress, setMappingInProgress] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    fetch('/api/evidence/upload-config', {
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => { if (cfg?.max_file_size_mb) setMaxFileSizeMb(cfg.max_file_size_mb); })
      .catch(() => {});
  }, []);

  /** Run measure-mapping for an already-uploaded (verified) evidence file.
   * Called automatically once evidence is accepted, or explicitly when the
   * user clicks "Accept anyway and continue" on a rejected file. */
  const mapEvidence = useCallback(async (
    evidence: Evidence,
    currentAiLinks: Array<{ sectionId: string; evidenceIds: string[] }>,
  ): Promise<{ evidence: Evidence; aiLinks: Array<{ sectionId: string; evidenceIds: string[] }> }> => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/evidence/${evidence.id}/map`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          system_standard_id: selectedStandard?.id,
          selected_sections: selectedSections || [],
          selected_subsections: selectedSubsections || {},
          language: i18n.language || 'en',
        }),
      });
      if (!response.ok) throw new Error((await response.json()).detail || t('createReview.uploadFailed'));
      const result = await response.json();
      const mapped: Evidence = { ...evidence, ...result };
      const mergedLinks = [...currentAiLinks];
      for (const sectionId of Object.keys(mapped.auto_detected_sections || {})) {
        const evidenceId = String(mapped.id);
        const idx = mergedLinks.findIndex((l) => l.sectionId === sectionId);
        if (idx >= 0) {
          if (!mergedLinks[idx].evidenceIds.includes(evidenceId)) {
            mergedLinks[idx] = { ...mergedLinks[idx], evidenceIds: [...mergedLinks[idx].evidenceIds, evidenceId] };
          }
        } else {
          mergedLinks.push({ sectionId, evidenceIds: [evidenceId] });
        }
      }
      return { evidence: mapped, aiLinks: mergedLinks };
    } catch (error) {
      toaster.create({
        title: t('createReview.evidenceMappingFailedTitle', { name: evidence.filename }),
        description: error instanceof Error ? error.message : t('createReview.failedToUploadEvidenceTryAgain'),
        type: 'error',
      });
      return { evidence, aiLinks: currentAiLinks };
    }
  }, [i18n.language, selectedSections, selectedStandard?.id, selectedSubsections, t]);

  const uploadSingleFile = useCallback(async (
    file: File,
    _currentEvidenceFiles: Evidence[],
    currentAiLinks: Array<{ sectionId: string; evidenceIds: string[] }>,
  ): Promise<{ evidence: Evidence | null; aiLinks: Array<{ sectionId: string; evidenceIds: string[] }> }> => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/markdown',
      'image/jpeg',
      'image/png',
      'image/gif',
    ];
    if (!allowedTypes.includes(file.type)) {
      toaster.create({ title: t('createReview.invalidFileType'), description: `${file.name}: ${t('createReview.uploadSupportedFileType')}`, type: 'error' });
      return { evidence: null, aiLinks: currentAiLinks };
    }
    if (file.size > maxFileSizeMb * 1024 * 1024) {
      toaster.create({ title: t('createReview.fileTooLarge'), description: `${file.name}: ${t('createReview.uploadFileSmallerThanMb', { size: maxFileSizeMb })}`, type: 'error' });
      return { evidence: null, aiLinks: currentAiLinks };
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedStandard?.id) formData.append('system_standard_id', String(selectedStandard.id));
      formData.append('selected_sections', JSON.stringify(selectedSections || []));
      formData.append('selected_subsections', JSON.stringify(selectedSubsections || {}));
      formData.append('language', i18n.language || 'en');
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/evidence/upload-temp', {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      if (!response.ok) throw new Error((await response.json()).detail || t('createReview.uploadFailed'));
      const result = await response.json();
      let uploaded: Evidence = {
        id: result.id?.toString() || Date.now().toString(),
        filename: file.name,
        file_size: file.size,
        file_type: file.type,
        uploaded_at: result.upload_time || new Date().toISOString(),
        content_summary: result.content_summary || t('createReview.evidenceFileSummary', { name: file.name }),
        expires_at: result.expires_at,
        ...result,
      };
      let mergedLinks = [...currentAiLinks];
      // Mapping to measures only runs automatically when evidence verification
      // accepted the file. If rejected, mapping is withheld until the user
      // explicitly clicks "Accept anyway and continue" on the evidence card.
      if (uploaded.verification_status !== 'rejected') {
        const mapped = await mapEvidence(uploaded, mergedLinks);
        uploaded = mapped.evidence;
        mergedLinks = mapped.aiLinks;
      }
      return { evidence: uploaded, aiLinks: mergedLinks };
    } catch (error) {
      toaster.create({
        title: `${file.name}: ${t('createReview.uploadFailed')}`,
        description: error instanceof Error ? error.message : t('createReview.failedToUploadEvidenceTryAgain'),
        type: 'error',
      });
      return { evidence: null, aiLinks: currentAiLinks };
    }
  }, [i18n.language, mapEvidence, maxFileSizeMb, selectedSections, selectedStandard?.id, selectedSubsections, t]);

  const handleFilesSelect = useCallback(async (files: File[]) => {
    if (!files.length) return;
    setIsUploading(true);
    setUploadProgress(0);
    let currentEvidence = [...evidenceFiles];
    let currentLinks = [...aiLinkedEvidence];
    let succeeded = 0;
    for (let i = 0; i < files.length; i++) {
      setUploadProgressLabel(files.length > 1 ? `${i + 1} / ${files.length}` : '');
      setUploadProgress(Math.round((i / files.length) * 100));
      const { evidence, aiLinks } = await uploadSingleFile(files[i], currentEvidence, currentLinks);
      if (evidence) {
        currentEvidence = [...currentEvidence, evidence];
        currentLinks = aiLinks;
        succeeded++;
      }
    }
    onEvidenceUpdate(currentEvidence);
    onAiLinksUpdate(currentLinks);
    setUploadProgress(100);
    if (succeeded > 0) {
      toaster.create({
        title: t('createReview.evidenceUploadedSuccessfully'),
        description: files.length > 1
          ? `${succeeded} / ${files.length} ${t('createReview.filesUploaded')}`
          : t('createReview.fileUploadedReadyForLinking', { name: files[0].name }),
        type: 'success',
      });
    }
    setIsUploading(false);
    setUploadProgress(0);
    setUploadProgressLabel('');
  }, [
    aiLinkedEvidence,
    evidenceFiles,
    onAiLinksUpdate,
    onEvidenceUpdate,
    t,
    uploadSingleFile,
  ]);

  /** Mark evidence as skipped (user chooses not to rely on it) without
   * treating it as accepted. Still mapped server-side so the assessment can
   * note the attempt against the relevant measure(s). */
  const skipEvidence = useCallback(async (
    evidence: Evidence,
    currentAiLinks: Array<{ sectionId: string; evidenceIds: string[] }>,
  ): Promise<{ evidence: Evidence; aiLinks: Array<{ sectionId: string; evidenceIds: string[] }> }> => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/evidence/${evidence.id}/skip`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          system_standard_id: selectedStandard?.id,
          selected_sections: selectedSections || [],
          selected_subsections: selectedSubsections || {},
          language: i18n.language || 'en',
        }),
      });
      if (!response.ok) throw new Error((await response.json()).detail || t('createReview.uploadFailed'));
      const result = await response.json();
      return { evidence: { ...evidence, ...result }, aiLinks: currentAiLinks };
    } catch (error) {
      toaster.create({
        title: t('createReview.evidenceMappingFailedTitle', { name: evidence.filename }),
        description: error instanceof Error ? error.message : t('createReview.failedToUploadEvidenceTryAgain'),
        type: 'error',
      });
      return { evidence, aiLinks: currentAiLinks };
    }
  }, [i18n.language, selectedSections, selectedStandard?.id, selectedSubsections, t]);

  /** User explicitly proceeds despite an AI rejection: run mapping for that
   * one evidence file and update the shared evidence/AI-links state. */
  const handleAcceptAnyway = useCallback(async (evidence: Evidence) => {
    setMappingInProgress((prev) => ({ ...prev, [evidence.id]: true }));
    try {
      const { evidence: mapped, aiLinks } = await mapEvidence(evidence, aiLinkedEvidence);
      onEvidenceUpdate(evidenceFiles.map((e) => (e.id === evidence.id ? mapped : e)));
      onAiLinksUpdate(aiLinks);
    } finally {
      setMappingInProgress((prev) => ({ ...prev, [evidence.id]: false }));
    }
  }, [aiLinkedEvidence, evidenceFiles, mapEvidence, onAiLinksUpdate, onEvidenceUpdate]);

  /** User chooses not to rely on this rejected evidence at all. Kept visible
   * (full attempt history) but flagged so it's excluded from scoring and
   * noted in the assessment instead. */
  const handleSkipEvidence = useCallback(async (evidence: Evidence) => {
    setMappingInProgress((prev) => ({ ...prev, [evidence.id]: true }));
    try {
      const { evidence: skipped, aiLinks } = await skipEvidence(evidence, aiLinkedEvidence);
      onEvidenceUpdate(evidenceFiles.map((e) => (e.id === evidence.id ? skipped : e)));
      onAiLinksUpdate(aiLinks);
    } finally {
      setMappingInProgress((prev) => ({ ...prev, [evidence.id]: false }));
    }
  }, [aiLinkedEvidence, evidenceFiles, onAiLinksUpdate, onEvidenceUpdate, skipEvidence]);

  /** User uploads a replacement file for a rejected evidence. The old file
   * is kept (full attempt history) but auto-skipped as superseded; the new
   * upload goes through the normal upload → verify → (auto-)map flow. */
  const [replacingEvidenceId, setReplacingEvidenceId] = useState<string | null>(null);
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);

  const handleReplaceClick = useCallback((evidence: Evidence) => {
    setReplacingEvidenceId(evidence.id);
    replaceFileInputRef.current?.click();
  }, []);

  const handleReplaceFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const oldEvidenceId = replacingEvidenceId;
    e.target.value = '';
    setReplacingEvidenceId(null);
    if (!file || !oldEvidenceId) return;

    const oldEvidence = evidenceFiles.find((ev) => ev.id === oldEvidenceId);
    if (!oldEvidence) return;

    setMappingInProgress((prev) => ({ ...prev, [oldEvidenceId]: true }));
    try {
      let currentEvidence = [...evidenceFiles];
      let currentLinks = [...aiLinkedEvidence];

      // Supersede the old attempt (kept visible, but excluded from scoring).
      const { evidence: skippedOld, aiLinks: linksAfterSkip } = await skipEvidence(oldEvidence, currentLinks);
      currentEvidence = currentEvidence.map((ev) => (ev.id === oldEvidenceId ? skippedOld : ev));
      currentLinks = linksAfterSkip;

      // Upload the replacement through the normal verify (+ auto-map) flow.
      const { evidence: newEvidence, aiLinks: linksAfterUpload } = await uploadSingleFile(file, currentEvidence, currentLinks);
      if (newEvidence) {
        currentEvidence = [...currentEvidence, newEvidence];
        currentLinks = linksAfterUpload;
        toaster.create({
          title: t('createReview.fileUploadedReadyForLinking', { name: file.name }),
          type: 'success',
        });
      }
      onEvidenceUpdate(currentEvidence);
      onAiLinksUpdate(currentLinks);
    } finally {
      setMappingInProgress((prev) => ({ ...prev, [oldEvidenceId]: false }));
    }
  }, [aiLinkedEvidence, evidenceFiles, onAiLinksUpdate, onEvidenceUpdate, replacingEvidenceId, skipEvidence, t, uploadSingleFile]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelect(Array.from(e.dataTransfer.files));
    }
  }, [handleFilesSelect]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelect(Array.from(e.target.files));
      // Reset so same files can be re-selected
      e.target.value = '';
    }
  }, [handleFilesSelect]);

  const handleRemoveEvidence = (evidenceId: string) => {
    const updatedEvidence = evidenceFiles.filter(e => e.id !== evidenceId);
    onEvidenceUpdate(updatedEvidence);
    
    // Remove from all links
    const updatedAiLinks = aiLinkedEvidence.map(link => ({
      ...link,
      evidenceIds: link.evidenceIds.filter(id => id !== evidenceId)
    })).filter(link => link.evidenceIds.length > 0);
    
    const updatedManualLinks = manualEvidenceLinks.map(link => ({
      ...link,
      evidenceIds: link.evidenceIds.filter(id => id !== evidenceId)
    })).filter(link => link.evidenceIds.length > 0);
    
    onAiLinksUpdate(updatedAiLinks);
    onManualLinksUpdate(updatedManualLinks);
  };

  const handleRemoveAiLink = (sectionId: string, evidenceId: string) => {
    const updatedLinks = aiLinkedEvidence.map(link =>
      link.sectionId === sectionId
        ? { ...link, evidenceIds: link.evidenceIds.filter(id => id !== evidenceId) }
        : link
    ).filter(link => link.evidenceIds.length > 0);
    onAiLinksUpdate(updatedLinks);
  };

  const handleToggleManualLink = (sectionId: string, evidenceId: string, typeKey?: string) => {
    const existingManualLink = manualEvidenceLinks.find(
      (link) => link.sectionId === sectionId && (link.typeKey || '') === (typeKey || '')
    );
    if (existingManualLink) {
      const updatedLinks = manualEvidenceLinks.map(link =>
        link.sectionId === sectionId && (link.typeKey || '') === (typeKey || '')
          ? {
              ...link,
              evidenceIds: link.evidenceIds.includes(evidenceId)
                ? link.evidenceIds.filter(id => id !== evidenceId)
                : [...link.evidenceIds, evidenceId],
            }
          : link
      ).filter(link => link.evidenceIds.length > 0);
      onManualLinksUpdate(updatedLinks);
    } else {
      const updatedLinks = [...manualEvidenceLinks, { sectionId, evidenceIds: [evidenceId], ...(typeKey ? { typeKey } : {}) }];
      onManualLinksUpdate(updatedLinks);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return t('createReview.fileSizeBytes', { count: 0 });
    const k = 1024;
    const sizes = [t('createReview.bytes'), t('createReview.kb'), t('createReview.mb'), t('createReview.gb')];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  const formatSectionHeading = (sectionId: string) => {
    if (sectionId.startsWith('goal-')) {
      const goalNo = sectionId.split('-')[1] || '';
      return `الغاية ${goalNo}`;
    }
    return sectionId;
  };
  const normalizeSelectionToken = (token: string) => String(token || '').split(':', 1)[0].trim();

  const linkTargets = useMemo<EvidenceLinkTarget[]>(() => {
    // Primary: use checklist data so target IDs match auto_detected_sections keys
    // (coverage_section) and all measure levels are visible, not just top-level subsections.
    if (requiredEvidenceData?.groups && requiredEvidenceData.groups.length > 0) {
      const targets: EvidenceLinkTarget[] = [];
      const seen = new Set<string>();
      for (const group of requiredEvidenceData.groups) {
        for (const measure of group.measures) {
          const id = measure.coverage_section;
          if (!seen.has(id)) {
            targets.push({
              id,
              label: measure.measure_title
                ? `${measure.measure_id} — ${measure.measure_title}`
                : measure.measure_id,
              parentLabel: group.section_title,
            });
            seen.add(id);
          }
        }
      }
      return targets;
    }

    // Fallback: build from selected sections/subsections (used before checklist loads)
    const targets: EvidenceLinkTarget[] = [];
    const seen = new Set<string>();

    for (const sectionId of selectedSections) {
      const section = sectionData.find((s) => s.id === sectionId);
      const sectionLabel = section?.title || formatSectionHeading(sectionId);
      const selectedTokens = Array.from(
        new Set((selectedSubsections?.[sectionId] || []).map(normalizeSelectionToken).filter(Boolean))
      );

      if (selectedTokens.length === 0) {
        if (!seen.has(sectionId)) {
          targets.push({ id: sectionId, label: sectionLabel, parentLabel: sectionLabel });
          seen.add(sectionId);
        }
        continue;
      }

      for (const token of selectedTokens) {
        if (token.includes('>')) {
          const [subsectionId, measureId] = token.split('>', 2);
          const subsection = section?.subsections?.find((sub) => sub.subsection_id === subsectionId);
          const measure = subsection?.sub_subsections?.find((m) => m.id === measureId);
          const measureText = (measure?.content || measure?.title || '').trim();
          const label = measureText ? `${measureId} - ${measureText}` : measureId;
          if (!seen.has(measureId)) {
            targets.push({ id: measureId, label, parentLabel: sectionLabel });
            seen.add(measureId);
          }
        } else {
          const subsection = section?.subsections?.find((sub) => sub.subsection_id === token);
          const subsectionText = (subsection?.subsection_title || subsection?.subsection_content || '').trim();
          const label = subsectionText ? `${token} - ${subsectionText}` : token;
          if (!seen.has(token)) {
            targets.push({ id: token, label, parentLabel: sectionLabel });
            seen.add(token);
          }
        }
      }
    }

    return targets;
  }, [requiredEvidenceData, sectionData, selectedSections, selectedSubsections]);

  const filteredEvidence = evidenceFiles.filter(evidence =>
    evidence.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const linkedEvidenceBySection = useMemo(() => {
    const evidenceById = new Map<string, Evidence>();
    for (const evidence of evidenceFiles) {
      evidenceById.set(String(evidence.id), evidence);
    }
    const map: { [sectionId: string]: Evidence[] } = {};
    const addLinks = (links: Array<{ sectionId: string; evidenceIds: string[] }>) => {
      for (const link of links) {
        const existing = map[link.sectionId] || [];
        const seenEvidenceIds = new Set(existing.map((evidence) => String(evidence.id)));
        for (const evId of link.evidenceIds || []) {
          const evidence = evidenceById.get(String(evId));
          if (evidence && !seenEvidenceIds.has(String(evidence.id))) {
            existing.push(evidence);
            seenEvidenceIds.add(String(evidence.id));
          }
        }
        if (existing.length > 0) map[link.sectionId] = existing;
      }
    };
    addLinks(aiLinkedEvidence);
    addLinks(manualEvidenceLinks);
    return map;
  }, [evidenceFiles, aiLinkedEvidence, manualEvidenceLinks]);

  const manualEvidenceBySectionType = useMemo(() => {
    const evidenceById = new Map<string, Evidence>();
    for (const evidence of evidenceFiles) {
      evidenceById.set(String(evidence.id), evidence);
    }
    const map: { [sectionId: string]: { [typeKey: string]: Evidence[] } } = {};
    for (const link of manualEvidenceLinks) {
      if (!link.typeKey) continue;
      const sectionMap = map[link.sectionId] || {};
      const existing = sectionMap[link.typeKey] || [];
      const seenEvidenceIds = new Set(existing.map((evidence) => String(evidence.id)));
      for (const evId of link.evidenceIds || []) {
        const evidence = evidenceById.get(String(evId));
        if (evidence && !seenEvidenceIds.has(String(evidence.id))) {
          existing.push(evidence);
          seenEvidenceIds.add(String(evidence.id));
        }
      }
      sectionMap[link.typeKey] = existing;
      map[link.sectionId] = sectionMap;
    }
    return map;
  }, [evidenceFiles, manualEvidenceLinks]);

  const requiredMeasureByCoverage = useMemo(() => {
    const map = new Map<string, ChecklistResponse['groups'][number]['measures'][number]>();
    for (const group of requiredEvidenceData?.groups || []) {
      for (const measure of group.measures) {
        map.set(measure.coverage_section, measure);
      }
    }
    return map;
  }, [requiredEvidenceData]);

  const getMatchedEvidenceForType = useCallback((coverageSection: string, typeKey: string) => {
    const linkedForSection = linkedEvidenceBySection[coverageSection] || [];
    const explicitManualMatches = manualEvidenceBySectionType[coverageSection]?.[typeKey] || [];
    if (typeKey === 'supporting_document') {
      return Array.from(new Map([...explicitManualMatches, ...linkedForSection].map((e) => [String(e.id), e])).values());
    }
    // Pool ALL evidence across every linked section so one file satisfies the
    // same required type across multiple measures (e.g. one approval record for all).
    const allLinked = Array.from(
      new Map(
        Object.values(linkedEvidenceBySection)
          .flat()
          .map((e) => [String(e.id), e])
      ).values()
    );
    const typeMatches = allLinked.filter((evidence) => {
      const sectionTypes = (evidence.auto_detected_sections?.[coverageSection]?.matched_types || []).filter(Boolean);
      if (sectionTypes.length > 0) return sectionTypes.includes(typeKey);
      return (evidence.evidence_types || []).includes(typeKey);
    });
    return Array.from(
      new Map([...explicitManualMatches, ...typeMatches].map((e) => [String(e.id), e])).values()
    );
  }, [linkedEvidenceBySection, manualEvidenceBySectionType]);

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <Heading size="md" mb={2}>
          {t('createReview.uploadEvidenceConfigureLinks')}
        </Heading>
        <Text color="gray.600">
          {t('createReview.uploadEvidenceConfigureLinksDescription')}
        </Text>
      </Box>

      {/* Required evidence checklist derived from selected measures */}
      <RequiredEvidenceChecklist
        selectedStandard={selectedStandard}
        selectedSections={selectedSections}
        selectedSubsections={selectedSubsections}
        linkedEvidenceBySection={linkedEvidenceBySection}
        manualEvidenceBySectionType={manualEvidenceBySectionType}
        onDataUpdate={setRequiredEvidenceData}
      />

      {/* Evidence Upload */}
      <Card.Root variant="outline">
        <Card.Header>
          <Heading size="sm">{t('createReview.uploadEvidenceFiles')}</Heading>
        </Card.Header>
        <Card.Body>
          <VStack gap={4}>
            <Card.Root variant="outline"
              border={dragActive ? '2px dashed' : '1px solid'}
              borderColor={dragActive ? 'brand.400' : 'gray.200'}
              bg={dragActive ? 'brand.50' : 'white'}
              transition="all 0.2s"
              cursor="pointer"
              onClick={() => document.getElementById('evidence-input')?.click()}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Card.Body>
                <VStack gap={4} py={6}>
                  <Icon
                    as={FiUpload}
                    w={10}
                    h={10}
                    color={dragActive ? 'brand.500' : 'gray.400'}
                  />
                  <VStack gap={2}>
                    <Text fontSize="md" fontWeight="semibold">
                      {dragActive ? t('createReview.dropEvidenceFilesHere') : t('createReview.clickUploadOrDragDrop')}
                    </Text>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      {t('createReview.evidenceSupportedFilesHelpDyn', { size: maxFileSizeMb })} — {t('createReview.multipleFilesSupported')}
                    </Text>
                  </VStack>
                  
                  {isUploading && (
                    <VStack gap={2} w="full">
                      <Text fontSize="sm" color="gray.600">
                        {uploadProgressLabel
                          ? `${t('createReview.uploading')} (${uploadProgressLabel})`
                          : t('createReview.uploading')}
                      </Text>
                      <Progress.Root value={uploadProgress}
                        size="sm"
                        w="full"
                        colorPalette="brand"
                        borderRadius="full"
                      />
                    </VStack>
                  )}
                </VStack>
              </Card.Body>
            </Card.Root>

            {/* Hidden file input */}
            <Input
              id="evidence-input"
              type="file"
              accept=".pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png,.gif"
              onChange={handleInputChange}
              multiple
              display="none"
            />
            {/* Hidden file input used for the "Replace file" action on a rejected evidence card */}
            <Input
              ref={replaceFileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png,.gif"
              onChange={handleReplaceFileChange}
              display="none"
            />
          </VStack>
        </Card.Body>
      </Card.Root>

      {/* Evidence Files List */}
      {evidenceFiles.length > 0 && (
        <Card.Root variant="outline">
          <Card.Header>
            <HStack justify="space-between">
              <Heading size="sm">{t('createReview.uploadedEvidenceCount', { count: evidenceFiles.length })}</Heading>
              <Box position="relative" maxW="300px">
                <Icon
                  as={FiSearch}
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                  zIndex={1}
                  pointerEvents="none"
                />
                <Input
                  placeholder={t('createReview.searchEvidence')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="sm"
                  pl="40px"
                />
              </Box>
            </HStack>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
              {filteredEvidence.map((evidence) => (
                <Card.Root key={evidence.id} variant="outline" size="sm">
                  <Card.Body>
                    <VStack gap={3} align="stretch">
                      <HStack justify="space-between">
                        <HStack gap={2}>
                          <Icon as={FiFile} w={4} h={4} color="brand.500" />
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium" lineClamp={1}>
                              {evidence.filename}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {formatFileSize(evidence.file_size)}
                            </Text>
                          </VStack>
                        </HStack>
                        <Button
                          variant="ghost"
                          colorPalette="red"
                          size="xs"
                          onClick={() => handleRemoveEvidence(evidence.id)}
                        >
                          <FiX />
                        </Button>
                      </HStack>
                      
                      {evidence.content_summary && (
                        <Text fontSize="xs" color="gray.600" lineClamp={2}>
                          {evidence.content_summary}
                        </Text>
                      )}

                      {evidence.verification_status && (
                        <Box
                          p={2}
                          bg={
                            evidence.skipped_by_user
                              ? 'gray.100'
                              : evidence.verification_status === 'rejected'
                                ? 'red.50'
                                : 'green.50'
                          }
                          borderRadius="md"
                          opacity={evidence.skipped_by_user ? 0.75 : 1}
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="semibold"
                            color={
                              evidence.skipped_by_user
                                ? 'gray.600'
                                : evidence.verification_status === 'rejected'
                                  ? 'red.700'
                                  : 'green.700'
                            }
                          >
                            {evidence.skipped_by_user
                              ? t('createReview.evidenceSkippedTitle')
                              : evidence.verification_status === 'rejected'
                                ? t('createReview.evidenceRejectedTitle')
                                : t('createReview.evidenceAcceptedTitle')}
                          </Text>
                          {evidence.verification_comments && (
                            <Text fontSize="xs" color="gray.700" mt={1}>
                              {evidence.verification_comments}
                            </Text>
                          )}
                          {(evidence.field_checks || []).length > 0 && (
                            <VStack align="start" gap={0.5} mt={2}>
                              {(evidence.field_checks || []).flatMap((entry) =>
                                entry.checks.map((c) => (
                                  <Text
                                    key={`${entry.type_key}-${c.field}`}
                                    fontSize="xs"
                                    color={c.found ? 'gray.500' : 'red.600'}
                                  >
                                    {c.found ? '✓' : '✗'} {entry.type_key}: {c.label || c.field}
                                    {c.note ? ` — ${c.note}` : ''}
                                  </Text>
                                ))
                              )}
                            </VStack>
                          )}
                          {evidence.verification_status === 'rejected' && !evidence.skipped_by_user && (
                            <HStack gap={2} mt={2} wrap="wrap">
                              <Button
                                size="xs"
                                colorPalette="blue"
                                variant="outline"
                                loading={!!mappingInProgress[evidence.id]}
                                onClick={() => handleReplaceClick(evidence)}
                              >
                                {t('createReview.replaceFileButton')}
                              </Button>
                              <Button
                                size="xs"
                                colorPalette="red"
                                variant="outline"
                                loading={!!mappingInProgress[evidence.id]}
                                onClick={() => handleAcceptAnyway(evidence)}
                              >
                                {t('createReview.acceptAnywayButton')}
                              </Button>
                              <Button
                                size="xs"
                                colorPalette="gray"
                                variant="ghost"
                                loading={!!mappingInProgress[evidence.id]}
                                onClick={() => handleSkipEvidence(evidence)}
                              >
                                {t('createReview.skipEvidenceButton')}
                              </Button>
                            </HStack>
                          )}
                        </Box>
                      )}

                      {typeof evidence.confidence_score === 'number' && evidence.verification_status !== 'rejected' && (
                        <Box
                          p={2}
                          bg={evidence.confidence_score === 0 ? 'orange.50' : 'green.50'}
                          borderRadius="md"
                        >
                          <Text fontSize="xs" fontWeight="semibold" color={evidence.confidence_score === 0 ? 'orange.700' : 'green.700'}>
                            {t('createReview.aiConfidence', {
                              score: Math.round((evidence.confidence_score || 0) * 100),
                            })}
                          </Text>
                          <Text fontSize="xs" color="gray.600" mt={1}>
                            {t('createReview.aiConfidenceReason', {
                              reason: evidence.confidence_reason || t('createReview.aiConfidencePending'),
                            })}
                          </Text>
                        </Box>
                      )}
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          </Card.Body>
        </Card.Root>
      )}

      {/* AI Analysis Toggle */}
      <Button
        variant="outline"
        onClick={() => setIsAiAnalysisOpen(!isAiAnalysisOpen)}
        alignSelf="start"
      >
        <Icon as={isAiAnalysisOpen ? FiEyeOff : FiEye} mr={2} />
        {isAiAnalysisOpen ? t('createReview.hideAiAnalysisManualLinking') : t('createReview.showAiAnalysisManualLinking')}
      </Button>

      {/* AI Analysis and Manual Linking */}
      {isAiAnalysisOpen && (
        <Card.Root variant="outline">
          <Card.Header>
            <Heading size="sm">{t('createReview.aiAnalysisManualLinking')}</Heading>
          </Card.Header>
          <Card.Body>
            <VStack gap={6} align="stretch">
              {/* AI Analysis Results */}
              {evidenceFiles.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={3}>
                    {t('createReview.aiSuggestedLinks')}
                  </Text>
                  <VStack gap={3} align="stretch">
                    {linkTargets.map((target) => {
                      const aiLinks = aiLinkedEvidence.find(link => link.sectionId === target.id);
                      const manualEvidenceIds = Array.from(
                        new Set(
                          manualEvidenceLinks
                            .filter((link) => link.sectionId === target.id)
                            .flatMap((link) => link.evidenceIds)
                        )
                      );
                      const requiredMeasure = requiredMeasureByCoverage.get(target.id);
                      
                      return (
                        <Card.Root key={target.id} variant="outline" size="sm">
                          <Card.Body>
                            <VStack gap={3} align="stretch">
                              <HStack justify="space-between">
                                <VStack align="start" gap={0}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {target.label}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {target.parentLabel}
                                  </Text>
                                </VStack>
                                <Badge colorPalette="blue" variant="subtle" fontSize="xs">
                                  {t('createReview.aiManualCount', { ai: aiLinks?.evidenceIds.length || 0, manual: manualEvidenceIds.length })}
                                </Badge>
                              </HStack>

                              {requiredMeasure && requiredMeasure.required_evidence.length > 0 && (
                                <Box>
                                  <Text fontSize="xs" color="gray.600" mb={2}>
                                    {t('createReview.requiredEvidenceInlineTitle')}
                                  </Text>
                                  <VStack gap={2} align="stretch">
                                    {requiredMeasure.required_evidence.map((requiredEvidence) => {
                                      const matchedEvidence = getMatchedEvidenceForType(target.id, requiredEvidence.type_key);
                                      const isSatisfied = matchedEvidence.length > 0;
                                      return (
                                        <Box
                                          key={`${target.id}-${requiredEvidence.type_key}`}
                                          p={2}
                                          borderWidth="1px"
                                          borderColor={isSatisfied ? 'green.200' : requiredEvidence.mandatory ? 'red.200' : 'blue.200'}
                                          bg={isSatisfied ? 'green.50' : requiredEvidence.mandatory ? 'red.50' : 'blue.50'}
                                          borderRadius="md"
                                        >
                                          <HStack justify="space-between" align="start" gap={2}>
                                            <VStack align="start" gap={0.5}>
                                              <Text fontSize="xs" fontWeight="medium">
                                                {requiredEvidence.label}
                                                {requiredEvidence.mandatory ? ' *' : ''}
                                              </Text>
                                              <Text fontSize="10px" color="gray.600">
                                                {requiredEvidence.guidance}
                                              </Text>
                                            </VStack>
                                            <Badge
                                              colorPalette={isSatisfied ? 'green' : requiredEvidence.mandatory ? 'red' : 'blue'}
                                              variant={isSatisfied || requiredEvidence.mandatory ? 'solid' : 'subtle'}
                                              fontSize="10px"
                                            >
                                              {isSatisfied
                                                ? t('createReview.requiredEvidenceMapped')
                                                : t('createReview.requiredEvidenceRemaining')}
                                            </Badge>
                                          </HStack>
                                          {matchedEvidence.length > 0 ? (
                                            <HStack gap={1} flexWrap="wrap" mt={2}>
                                              {matchedEvidence.map((evidence) => (
                                                <Badge key={`${requiredEvidence.type_key}-${evidence.id}`} colorPalette="green" variant="subtle" fontSize="10px">
                                                  {evidence.filename}
                                                </Badge>
                                              ))}
                                            </HStack>
                                          ) : (
                                            <Text fontSize="10px" color="gray.600" mt={2}>
                                              {t('createReview.requiredEvidenceNoFilesMapped')}
                                            </Text>
                                          )}

                                          {evidenceFiles.length > 0 && (
                                            <Box mt={2}>
                                              <Text fontSize="10px" color="gray.600" mb={1}>
                                                {t('createReview.requiredEvidenceManualAssignLabel')}
                                              </Text>
                                              <VStack gap={1} align="stretch">
                                                {evidenceFiles.map((evidence) => {
                                                  const isTypeLinked = manualEvidenceLinks.some(
                                                    (link) =>
                                                      link.sectionId === target.id
                                                      && link.typeKey === requiredEvidence.type_key
                                                      && link.evidenceIds.includes(evidence.id)
                                                  );
                                                  return (
                                                    <HStack
                                                      key={`${target.id}-${requiredEvidence.type_key}-${evidence.id}`}
                                                      gap={2}
                                                      p={2}
                                                      bg={isTypeLinked ? 'orange.50' : 'gray.50'}
                                                      borderRadius="md"
                                                    >
                                                      <Icon as={FiFile} w={3} h={3} color={isTypeLinked ? 'orange.500' : 'gray.500'} />
                                                      <Text fontSize="xs" flex={1}>
                                                        {evidence.filename}
                                                      </Text>
                                                      <Button
                                                        size="xs"
                                                        variant="ghost"
                                                        colorPalette={isTypeLinked ? 'red' : 'green'}
                                                        onClick={() => handleToggleManualLink(target.id, evidence.id, requiredEvidence.type_key)}
                                                      >
                                                        {isTypeLinked ? <FiX /> : <FiCheck />}
                                                      </Button>
                                                    </HStack>
                                                  );
                                                })}
                                              </VStack>
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </VStack>
                                </Box>
                              )}
                               
                              {/* AI Suggested Evidence */}
                              {aiLinks && aiLinks.evidenceIds.length > 0 && (
                                <Box>
                                  <Text fontSize="xs" color="gray.600" mb={2}>
                                    {t('createReview.aiSuggestedEvidence')}
                                  </Text>
                                  <VStack gap={1} align="stretch">
                                    {aiLinks.evidenceIds.map((evidenceId) => {
                                      const evidence = evidenceFiles.find(e => e.id === evidenceId);
                                      if (!evidence) return null;
                                      const linkDetails = evidence.auto_detected_sections?.[target.id];
                                      const confidencePercent = typeof linkDetails?.confidence === 'number'
                                        ? Math.round(linkDetails.confidence * 100)
                                        : null;
                                      const matchedTypes = (linkDetails?.matched_types || []).filter(Boolean);

                                      return (
                                        <Box key={evidenceId} p={2} bg="blue.50" borderRadius="md">
                                          <HStack gap={2}>
                                            <Icon as={FiFile} w={3} h={3} color="blue.500" />
                                            <Text fontSize="xs" flex={1}>
                                              {evidence.filename}
                                            </Text>
                                            <Button
                                              size="xs"
                                              variant="ghost"
                                              colorPalette="red"
                                              onClick={() => handleRemoveAiLink(target.id, evidenceId)}
                                            >
                                              <FiX />
                                            </Button>
                                          </HStack>
                                          {(confidencePercent !== null || matchedTypes.length > 0 || linkDetails?.reason) && (
                                            <VStack align="start" gap={0.5} pl={5} mt={1}>
                                              {confidencePercent !== null && (
                                                <Text fontSize="2xs" color="blue.700">
                                                  {t('createReview.aiConfidence', { score: confidencePercent })}
                                                </Text>
                                              )}
                                              {matchedTypes.length > 0 && (
                                                <Text fontSize="2xs" color="blue.700">
                                                  {t('createReview.aiMatchedEvidenceTypes', { types: matchedTypes.join(', ') })}
                                                </Text>
                                              )}
                                              {linkDetails?.reason && (
                                                <Text fontSize="2xs" color="blue.700">
                                                  {t('createReview.aiConfidenceReason', { reason: linkDetails.reason })}
                                                </Text>
                                              )}
                                            </VStack>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </VStack>
                                </Box>
                              )}
                              
                              {/* Manual Evidence Links */}
                              <Box>
                                <Text fontSize="xs" color="gray.600" mb={2}>
                                  {t('createReview.manuallyLinkEvidence')}
                                </Text>
                                <VStack gap={2} align="stretch">
                                  {evidenceFiles.map((evidence) => {
                                    const isLinked = manualEvidenceLinks.some(
                                      (link) => link.sectionId === target.id && !link.typeKey && link.evidenceIds.includes(evidence.id)
                                    );
                                    return (
                                      <HStack key={evidence.id} gap={2} p={2} bg={isLinked ? "orange.50" : "gray.50"} borderRadius="md">
                                        <Icon as={FiFile} w={3} h={3} color={isLinked ? "orange.500" : "gray.500"} />
                                        <Text fontSize="xs" flex={1}>
                                          {evidence.filename}
                                        </Text>
                                        <Button
                                          size="xs"
                                          variant="ghost"
                                          colorPalette={isLinked ? "red" : "green"}
                                          onClick={() => handleToggleManualLink(target.id, evidence.id)}
                                        >
                                          {isLinked ? <FiX /> : <FiCheck />}
                                        </Button>
                                      </HStack>
                                    );
                                  })}
                                </VStack>
                              </Box>
                            </VStack>
                          </Card.Body>
                        </Card.Root>
                      );
                    })}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Card.Body>
        </Card.Root>
      )}

      {/* Guidelines */}
      <Alert.Root status="info" borderRadius="md">
        <Alert.Indicator>
          <Icon as={FiInfo} />
        </Alert.Indicator>
        <Box>
          <Alert.Title>{t('createReview.evidenceUploadGuidelines')}</Alert.Title>
          <Alert.Description>
            • {t('createReview.guidelineUploadRelevantDocuments')}<br />
            • {t('createReview.guidelineAiSuggestsLinks')}<br />
            • {t('createReview.guidelineManualOverride')}<br />
            • {t('createReview.guidelineEvidenceProcessedSecurely')}
          </Alert.Description>
        </Box>
      </Alert.Root>

    </VStack>
  );
};

export default EvidenceUploadStep; 
